import React, { useState, memo, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import {
  LocationOnRounded as LocationIcon,
  HotelRounded as HotelIcon,
  RestaurantRounded as RestaurantIcon,
  LocalActivityRounded as ActivityIcon,
  CheckCircleRounded as CheckIcon,
  DiamondRounded as PremiumIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import AIResultsCardSlider from './AIResultsCardSlider';
import PaymentWall from './PaymentWall';
import { THEME_COLORS, THEME_GRADIENTS } from '../constants/theme';
import usePremiumAI from '../hooks/usePremiumAI';
import googleAPIService from '../services/googleAPIService';
import { useBypass } from '../contexts/BypassContext';
import api from '../config/api';

const AI_CONCIERGE_PLANNING_TRIP_KEY = 'ai_concierge_planning_trip_id';

const PremiumAIConcierge = ({ preferences, onTripCreated }) => {
  const [planningTripId, setPlanningTripId] = useState(() => {
    const stored = localStorage.getItem(AI_CONCIERGE_PLANNING_TRIP_KEY);
    return stored ? Number(stored) : null;
  });

  const {
    recommendations,
    isGenerating,
    isConverting,
    error,
    buttonState,
    generateRecommendations,
    cancelGeneration,
    selectOption,
    getSelectedOption,
    getSelectedItinerary,
    convertToTrip,
    clearSelections,
    resetRecommendations,
    clearAllState
  } = usePremiumAI(preferences, { planningTripId });

  const { bypassEnabled, isDevelopment } = useBypass();
  const [activeStep, setActiveStep] = useState(0);
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [accessExpired, setAccessExpired] = useState(false);
  const [expirationReason, setExpirationReason] = useState('');
  const [expirationDetails, setExpirationDetails] = useState({});
  const [googleData, setGoogleData] = useState(null);
  const [googleDataLoading, setGoogleDataLoading] = useState(false);
  const [googleDataError, setGoogleDataError] = useState(null);

  // Load Google data from localStorage on component mount
  useEffect(() => {
    const savedGoogleData = localStorage.getItem('premium_ai_google_data');
    if (savedGoogleData) {
      try {
        const parsedData = JSON.parse(savedGoogleData);
        setGoogleData(parsedData);
        console.log('📊 Loaded Google data from localStorage:', parsedData);
      } catch (error) {
        console.error('Error parsing saved Google data:', error);
        localStorage.removeItem('premium_ai_google_data');
      }
    }
  }, []);

  // Save Google data to localStorage whenever it changes
  useEffect(() => {
    if (googleData) {
      localStorage.setItem('premium_ai_google_data', JSON.stringify(googleData));
      console.log('💾 Saved Google data to localStorage');
    }
  }, [googleData]);

  // Cleanup function to clear localStorage when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if we're not just switching tabs but actually leaving the page
      // This will be handled by the parent component or page navigation
      console.log('🧹 PremiumAIConcierge component unmounting');
    };
  }, []);

  const steps = ['Generate Recommendations', 'Select Options', 'Review & Convert'];

  const ensurePlanningTrip = useCallback(async () => {
    const { toLocation, startDate, endDate, tripTypes } = preferences;
    if (!toLocation || !startDate || !endDate) {
      return null;
    }

    const existingTripId = planningTripId || localStorage.getItem(AI_CONCIERGE_PLANNING_TRIP_KEY);
    const response = await api.post('/payments/ai-concierge/planning-trip', {
      title: `${toLocation} - AI Planning`,
      startDate,
      endDate,
      location: toLocation,
      tripType: tripTypes?.[0] || 'vacation',
      existingTripId: existingTripId ? Number(existingTripId) : undefined
    });
    const tripId = response.data?.tripId;
    if (tripId) {
      localStorage.setItem(AI_CONCIERGE_PLANNING_TRIP_KEY, String(tripId));
      setPlanningTripId(tripId);
    }
    return tripId || null;
  }, [preferences, planningTripId]);

  // Check AI concierge access - consolidated into single useEffect
  useEffect(() => {
        console.log('🚀 PremiumAIConcierge useEffect triggered');
        console.log('   - bypassEnabled:', bypassEnabled);
        console.log('   - isDevelopment:', isDevelopment);
    
    const checkAIAccess = async () => {
      // If bypass is enabled in development, grant access immediately
      if (isDevelopment && bypassEnabled) {
        console.log('🚫 Bypassing AI concierge access check - development mode');
        setHasAIAccess(true);
        setShowPaywall(false);
        setCheckingAccess(false);
        return;
      }

      try {
        if (!preferences.toLocation || !preferences.startDate || !preferences.endDate) {
          setHasAIAccess(false);
          setCheckingAccess(false);
          return;
        }

        const tripId = await ensurePlanningTrip();
        if (!tripId) {
          setHasAIAccess(false);
          setCheckingAccess(false);
          return;
        }

        console.log('🔍 Checking AI concierge access...');
        const response = await api.get(`/payments/check-ai-concierge-access?tripId=${tripId}`);
        console.log('   - API response received:', response.data);
        
        const { hasAccess, reason, accessEndsAt } = response.data;
        
        console.log('🔍 AI Concierge access check result:', { hasAccess, reason });
        
        setHasAIAccess(hasAccess);
        setAccessExpired(Boolean(reason === 'trip_ended'));
        setExpirationReason(reason || '');
        setExpirationDetails({ accessEndsAt });
        
        // Set paywall visibility based on access
        if (hasAccess) {
          console.log('✅ AI concierge access granted - hiding paywall');
        } else {
          console.log('❌ AI concierge access denied - showing paywall');
        }
        
        // Log access details for debugging
        if (reason === 'trip_ended') {
          console.log('AI concierge access ended for this trip');
        }
      } catch (error) {
        console.error('❌ Error checking AI concierge access:', error);
        console.error('   - Error details:', error.response?.data || error.message);
        setHasAIAccess(false);
        setAccessExpired(false);
        setExpirationReason('');
        setExpirationDetails({});
        // Don't automatically show paywall on error - only show when user tries to generate
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAIAccess();
      }, [bypassEnabled, isDevelopment, preferences.toLocation, preferences.startDate, preferences.endDate, planningTripId, ensurePlanningTrip]);

  // Handle paywall visibility - only show when user tries to generate
  useEffect(() => {
    if (hasAIAccess) {
      setShowPaywall(false);
    }
    // Don't automatically show paywall - only show when user clicks "Generate My Trip"
  }, [hasAIAccess]);

  const handleGenerateRecommendations = async (skipAccessCheck = false) => {
    if (!skipAccessCheck && !hasAIAccess) {
      try {
        await ensurePlanningTrip();
      } catch (error) {
        console.error('Failed to prepare planning trip:', error);
      }
      setShowPaywall(true);
      return;
    }

    // Validate required fields
    if (!preferences.toLocation || !preferences.startDate || !preferences.endDate) {
      return;
    }

    try {
      // Fetch Google data first for premium users
      if (preferences.fromLocation && preferences.toLocation) {
        console.log('🔍 Premium AI - Fetching Google data for:', { 
          fromLocation: preferences.fromLocation, 
          toLocation: preferences.toLocation 
        });
        
        setGoogleDataLoading(true);
        setGoogleDataError(null);
        
        const travelData = await googleAPIService.fetchTravelData(
          preferences.fromLocation, 
          preferences.toLocation, 
          preferences.startDate, 
          preferences.endDate, 
          { ...preferences, adults: preferences.travelers || 1 }
        );
        
        console.log('📊 Premium AI - Google data fetched:', travelData);
        setGoogleData(travelData);
        setGoogleDataLoading(false);
      }
      
      // Start generation - this will set isGenerating to true
      await generateRecommendations();
      // Note: setActiveStep(1) will be handled by useEffect when results are available
    } catch (error) {
      console.error('Generation failed:', error);
      setGoogleDataError(error.message);
      setGoogleDataLoading(false);
      // Error handling is done in the hook
    }
  };

  const handlePaywallSuccess = async () => {
    setShowPaywall(false);
    try {
      const tripId = planningTripId || await ensurePlanningTrip();
      if (tripId) {
        const response = await api.get(`/payments/check-ai-concierge-access?tripId=${tripId}`);
        setHasAIAccess(Boolean(response.data?.hasAccess));
        if (!response.data?.hasAccess) {
          return;
        }
      } else {
        setHasAIAccess(true);
      }
    } catch (error) {
      setHasAIAccess(true);
    }
    await handleGenerateRecommendations(true);
  };


  const handleConvertToTrip = async () => {
    try {
      const selectedItinerary = getSelectedItinerary();
      const tripData = await convertToTrip(selectedItinerary);
      setShowConversionDialog(true);
      onTripCreated?.(tripData);
    } catch (err) {
      console.error('Conversion failed:', err);
    }
  };

  const handleStartOver = () => {
    resetRecommendations();
    clearAllState();
    setActiveStep(0);
    // Clear Google data when starting over
    setGoogleData(null);
    setGoogleDataLoading(false);
    setGoogleDataError(null);
    // Clear from localStorage as well
    localStorage.removeItem('premium_ai_google_data');
  };

  // Auto-advance to step 1 when results are available
  useEffect(() => {
    if (recommendations && activeStep === 0) {
      console.log('🎉 Results available, advancing to step 1');
      console.log('🎉 Recommendations data:', recommendations);
      setActiveStep(1);
    }
  }, [recommendations, activeStep]);

  // Force advance if we have results but are still on step 0
  useEffect(() => {
    if (recommendations && activeStep === 0 && !isGenerating) {
      console.log('🔄 Force advancing to step 1 - results available but not generating');
      setActiveStep(1);
    }
  }, [recommendations, activeStep, isGenerating]);

  // Show processing state when generating
  useEffect(() => {
    console.log('🔍 PremiumAIConcierge state:', { isGenerating, activeStep, hasRecommendations: !!recommendations });
    
    if (isGenerating && activeStep === 0) {
      console.log('🔄 AI is generating, staying on step 0');
    }
  }, [isGenerating, activeStep, recommendations]);


  const renderDayItinerary = (day) => {
    const categories = [
      { key: 'locations', label: 'Locations', icon: <LocationIcon /> },
      { key: 'restaurants', label: 'Restaurants', icon: <RestaurantIcon /> },
      { key: 'activities', label: 'Activities', icon: <ActivityIcon /> },
      { key: 'hotels', label: 'Accommodation', icon: <HotelIcon /> }
    ];

    return (
      <Paper key={day.day} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Day {day.day}
        </Typography>

        {categories.map(category => {
          const items = day[category.key];
          if (!items || items.length === 0) return null;

          return (
            <AIResultsCardSlider
              key={category.key}
              title={category.label}
              items={items}
              category={category.key}
              dayIndex={day.day - 1}
              onSelect={selectOption}
              selectedIndices={getSelectedOption(category.key, day.day - 1) || []}
              maxVisible={3}
            />
          );
        })}
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: { xs: 0.5, md: 1 }, 
          mb: { xs: 1.5, md: 2 },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <PremiumIcon sx={{ 
            color: THEME_COLORS.primary.main, 
            fontSize: { xs: '1.5rem', md: '2rem' } 
          }} />
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            background: THEME_GRADIENTS.primary, 
            WebkitBackgroundClip: 'text', 
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}>
            Premium AI Concierge
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ 
          maxWidth: { xs: '100%', md: 600 }, 
          mx: 'auto', 
          mb: 2,
          px: { xs: 1, md: 0 },
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}>
          Get personalized trip recommendations with options for each category, 
          scheduled over your trip period, and convert to a real trip in MyTrips.
        </Typography>
        
        {/* Access Expiration Message */}
        {accessExpired && (
          <Alert 
            severity="warning" 
            sx={{ 
              maxWidth: 600, 
              mx: 'auto', 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              ⏰ AI Concierge Access Expired
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {expirationReason === 'expired_daily' 
                ? `Your access expired after ${expirationDetails.daysSincePurchase} day(s).`
                : `Your access expired after creating ${expirationDetails.tripsCreatedSince} trip(s).`
              }
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              💳 New payment required to continue using AI Concierge features.
            </Typography>
          </Alert>
        )}
        
        
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={handleStartOver}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1 },
              fontSize: { xs: '0.9rem', md: '1rem' },
              borderColor: THEME_COLORS.primary.main,
              color: THEME_COLORS.primary.main,
              '&:hover': {
                backgroundColor: THEME_COLORS.primary.main,
                color: 'white',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              }
            }}
          >
            Start Over
          </Button>
        )}
      </Box>

      {/* Progress Stepper */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel
          sx={{
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Step 1: Generate Recommendations */}
      {activeStep === 0 && (
        <Box sx={{ textAlign: 'center' }}>
          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to Generate Your Personalized Trip?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Our AI will analyze your preferences and create options for locations, 
              hotels, restaurants, and activities, all scheduled over your trip period.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>⏱️ Processing Time:</strong> AI recommendation generation typically takes 1-2 minutes. 
                This ensures high-quality, personalized suggestions for your trip.
              </Typography>
            </Alert>
            
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateRecommendations}
              disabled={buttonState.disabled || !preferences.toLocation || !preferences.startDate || !preferences.endDate}
              startIcon={!buttonState.showLoading ? <PremiumIcon /> : null}
              sx={{
                background: THEME_GRADIENTS.primary,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                minHeight: 48,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                },
                '&:disabled': {
                  background: 'linear-gradient(145deg, #b09090, #a08080)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  transform: 'none',
                  boxShadow: 'none',
                  cursor: 'not-allowed',
                }
              }}
            >
              {buttonState.showLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {buttonState.text}
                  </Typography>
                </Box>
              ) : (
                checkingAccess ? 'Checking Access...' :
                (!preferences.toLocation || !preferences.startDate || !preferences.endDate) ? 
                'Fill in destination and dates first' : buttonState.text
              )}
            </Button>
            
            {/* Cancel button - separate from main button */}
            {buttonState.showLoading && (
              <Button
                variant="outlined"
                size="small"
                onClick={cancelGeneration}
                sx={{
                  mt: 2,
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Cancel Generation
              </Button>
            )}
          </Paper>
        </Box>
      )}

      {/* Step 2: Select Options */}
      {activeStep === 1 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'center' }, 
            mb: 3,
            gap: { xs: 2, md: 0 }
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: { xs: 1, md: 0 } }}>
                Select Your Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: { xs: 'block', md: 'none' },
                fontSize: '0.9rem'
              }}>
                Select multiple options from each category
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' }, 
              gap: { xs: 1.5, sm: 2 },
              width: { xs: '100%', md: 'auto' }
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: { xs: 'none', md: 'block' },
                fontSize: '0.9rem'
              }}>
                Select multiple options from each category
              </Typography>
              <Button
                variant="outlined"
                onClick={clearSelections}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 }
                }}
              >
                Clear All Selections
              </Button>
              <Button
                variant="outlined"
                onClick={handleStartOver}
                sx={{ 
                  borderRadius: 2,
                  borderColor: THEME_COLORS.primary.main,
                  color: THEME_COLORS.primary.main,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  '&:hover': {
                    backgroundColor: THEME_COLORS.primary.main,
                    color: 'white'
                  }
                }}
              >
                Start Over
              </Button>
            </Box>
          </Box>

          {/* Make your Bookings Section */}
          {googleDataLoading && (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Fetching booking options...
              </Typography>
            </Box>
          )}
          
          {googleDataError && (
            <Box sx={{ mb: 4 }}>
              <Alert severity="warning">
                Unable to fetch booking options: {googleDataError}
              </Alert>
            </Box>
          )}
          
          {googleData && (
            <Accordion 
              defaultExpanded={
                googleData.flights?.length > 0 || 
                googleData.hotels?.length > 0 || 
                googleData.restaurants?.length > 0 || 
                googleData.attractions?.length > 0
              } 
              sx={{ mb: 4 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  Make your Bookings
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Flights */}
                {googleData.flights && googleData.flights.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      ✈️ Flights
                    </Typography>
                    {googleData.flights.slice(0, 5).map((flight, index) => (
                      <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Link
                          href={`https://www.google.com/flights/search?q=${flight.departure}+to+${flight.arrival}+${preferences.startDate}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                          sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {flight.airline} - {flight.price}
                          </Typography>
                        </Link>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {flight.departure} → {flight.arrival} • {flight.duration} • {flight.stops}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          href={`https://www.google.com/flights/search?q=${flight.departure}+to+${flight.arrival}+${preferences.startDate}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto',
                            textTransform: 'none'
                          }}
                        >
                          Book Flight
                        </Button>
                      </Box>
                    ))}
                  </Card>
                )}

                {/* Hotels */}
                {googleData.hotels && googleData.hotels.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      🏨 Hotels
                    </Typography>
                    {googleData.hotels.slice(0, 5).map((hotel, index) => (
                      <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Link
                          href={`https://www.google.com/travel/hotels/search?q=${encodeURIComponent(hotel.name + ' ' + hotel.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                          sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {hotel.name} - {hotel.price}
                          </Typography>
                        </Link>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          ⭐ {hotel.rating} • {hotel.address}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          href={`https://www.google.com/travel/hotels/search?q=${encodeURIComponent(hotel.name + ' ' + hotel.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto',
                            textTransform: 'none'
                          }}
                        >
                          Book Hotel
                        </Button>
                      </Box>
                    ))}
                  </Card>
                )}

                {/* Restaurants */}
                {googleData.restaurants && googleData.restaurants.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      🍽️ Restaurants
                    </Typography>
                    {googleData.restaurants.slice(0, 5).map((restaurant, index) => (
                      <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Link
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.name + ' ' + restaurant.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                          sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {restaurant.name} - {restaurant.price}
                          </Typography>
                        </Link>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          ⭐ {restaurant.rating} • {restaurant.cuisine}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.name + ' ' + restaurant.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto',
                            textTransform: 'none'
                          }}
                        >
                          View on Maps
                        </Button>
                      </Box>
                    ))}
                  </Card>
                )}

                {/* Attractions */}
                {googleData.attractions && googleData.attractions.length > 0 && (
                  <Card sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      🎯 Attractions
                    </Typography>
                    {googleData.attractions.slice(0, 5).map((attraction, index) => (
                      <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Link
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(attraction.name + ' ' + attraction.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                          sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {attraction.name} - {attraction.price}
                          </Typography>
                        </Link>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          ⭐ {attraction.rating} • {attraction.type}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(attraction.name + ' ' + attraction.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto',
                            textTransform: 'none'
                          }}
                        >
                          View on Maps
                        </Button>
                      </Box>
                    ))}
                  </Card>
                )}
                </Box>
                
                {/* Show message when no results */}
                {(!googleData.flights || googleData.flights.length === 0) && 
                 (!googleData.hotels || googleData.hotels.length === 0) && 
                 (!googleData.restaurants || googleData.restaurants.length === 0) && 
                 (!googleData.attractions || googleData.attractions.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No booking options found for your selected locations.
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {!recommendations ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                No recommendations available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please go back and generate recommendations first.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setActiveStep(0)}
                sx={{ borderRadius: 2 }}
              >
                Back to Generate
              </Button>
            </Paper>
          ) : (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActivityIcon color="primary" />
                Build Your Itinerary
              </Typography>
              {recommendations.scheduledItinerary?.map(renderDayItinerary)}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setActiveStep(2)}
              sx={{
                background: THEME_GRADIENTS.primary,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                minHeight: 48
              }}
            >
              Review & Convert to Trip
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 3: Review & Convert */}
      {activeStep === 2 && (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Review Your Selected Trip
          </Typography>
          
          {/* Show Selected Options Summary */}
          {recommendations && (
            <Box sx={{ mb: 3 }}>
              {getSelectedItinerary()?.map((day, dayIndex) => (
                <Card key={dayIndex} sx={{ mb: 2, p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Day {day.day} - {day.city}
                  </Typography>
                  
                  {day.locations.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Selected Locations ({day.locations.length}):
                      </Typography>
                      {day.locations.map((location, index) => (
                        <Typography key={index} variant="body2" sx={{ ml: 3, mb: 0.5 }}>
                          • {location.name} - {location.description}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  
                  {day.restaurants.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <RestaurantIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Selected Restaurants ({day.restaurants.length}):
                      </Typography>
                      {day.restaurants.map((restaurant, index) => (
                        <Typography key={index} variant="body2" sx={{ ml: 3, mb: 0.5 }}>
                          • {restaurant.name} - {restaurant.description}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  
                  {day.activities.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <ActivityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Selected Activities ({day.activities.length}):
                      </Typography>
                      {day.activities.map((activity, index) => (
                        <Typography key={index} variant="body2" sx={{ ml: 3, mb: 0.5 }}>
                          • {activity.name} - {activity.description}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  
                  {day.hotels && day.hotels.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <HotelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Selected Hotels ({day.hotels.length}):
                      </Typography>
                      {day.hotels.map((hotel, index) => (
                        <Typography key={index} variant="body2" sx={{ ml: 3, mb: 0.5 }}>
                          • {hotel.name} - {hotel.description}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Card>
              ))}
            </Box>
          )}
          
          
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: 'success.light', color: 'success.contrastText' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              🎉 Your Personalized Trip is Ready!
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You've selected the perfect combination of locations, restaurants, activities, and accommodations. 
              Click below to convert this into a real trip in MyTrips.
            </Typography>
            
            <Box sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.2)', 
              borderRadius: 1, 
              p: 2, 
              border: '1px solid rgba(255, 255, 255, 0.3)',
              mt: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, display: 'flex', alignItems: 'center' }}>
                📋 Important Note:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                This is an itinerary plan only. All bookings (hotels, restaurants, activities) must be made manually 
                through the respective service providers. We recommend confirming availability and making reservations 
                before your trip.
              </Typography>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(1)}
              sx={{ borderRadius: 2 }}
            >
              Back to Selection
            </Button>
            <Button
              variant="outlined"
              onClick={handleStartOver}
              sx={{ 
                borderRadius: 2,
                borderColor: THEME_COLORS.primary.main,
                color: THEME_COLORS.primary.main,
                '&:hover': {
                  backgroundColor: THEME_COLORS.primary.main,
                  color: 'white'
                }
              }}
            >
              Start Over
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleConvertToTrip}
              disabled={isConverting}
              startIcon={isConverting ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{
                background: THEME_GRADIENTS.primary,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                minHeight: 48
              }}
            >
              {isConverting ? 'Creating Trip & Itinerary...' : 'Create Trip in MyTrips'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Conversion Success Dialog */}
      <Dialog open={showConversionDialog} onClose={() => setShowConversionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <CheckIcon sx={{ color: 'success.main', fontSize: '3rem', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Trip Created Successfully!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Your personalized trip has been created and added to MyTrips. 
            You can now view the full itinerary, share it with your group, and start planning!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowConversionDialog(false);
              resetRecommendations();
              setActiveStep(0);
            }}
            sx={{
              borderColor: THEME_COLORS.primary.main,
              color: THEME_COLORS.primary.main,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: THEME_COLORS.primary.main,
                color: 'white'
              }
            }}
          >
            Create Another Trip
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowConversionDialog(false);
              onTripCreated?.(); // Navigate to trip details
            }}
            sx={{
              background: THEME_GRADIENTS.primary,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            View Trip Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Concierge Paywall */}
      <PaymentWall
        open={showPaywall}
        onClose={() => {
          console.log('🔴 PremiumAIConcierge onClose called');
          console.log('   - showPaywall before:', showPaywall);
          setShowPaywall(false);
          console.log('🔴 PremiumAIConcierge onClose completed');
        }}
        tripId={planningTripId}
        tripTitle={preferences.toLocation ? `${preferences.toLocation} - AI Planning` : 'AI Concierge Service'}
        isOwner={true}
        feature="AI Concierge"
        tierType="ai_concierge"
        onSuccess={handlePaywallSuccess}
      />
    </Container>
  );
};

export default memo(PremiumAIConcierge);
